import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, CircularProgress, Alert, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  FormControlLabel, Checkbox, Container,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { useAuth } from '../../context/AuthContext';
import { getTeam, listTeamStaff } from '../../services/teamService';
import { hasProgramManagementAuthorityForTeam } from '../../services/programService';
import { listTeamBoardPosts, createTeamBoardPost, updateTeamBoardPost, deleteTeamBoardPost } from '../../services/teamBoardService';
import { CATEGORY_LABELS, isPostEdited } from '../../utils/teamBoard';
import TeamHubLayout from '../../components/teams/TeamHubLayout';

const EMPTY_FORM = { category: 'announcement', title: '', body: '', isPinned: false };

function postToForm(post) {
  return { category: post.category, title: post.title, body: post.body, isPinned: post.is_pinned };
}

/** Create/Edit Post — one form, reused for both (post=null means
 * create), same pattern as Schedule's EventFormDialog. Client-side
 * validation mirrors create_team_board_post()/update_team_board_post()'s
 * own rules (031); the RPC remains the real authority. */
function PostFormDialog({ open, onClose, onSaved, teamId, post }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(post ? postToForm(post) : EMPTY_FORM);
      setError(null);
    }
  }, [open, post]);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.title.trim()) { setError('Title is required.'); return; }
    if (!form.body.trim()) { setError('Message is required.'); return; }

    setSaving(true);
    try {
      const payload = {
        category: form.category,
        title: form.title.trim(),
        body: form.body.trim(),
        isPinned: form.isPinned,
      };
      if (post) {
        await updateTeamBoardPost({ postId: post.id, ...payload });
      } else {
        await createTeamBoardPost({ teamId, ...payload });
      }
      await onSaved();
      onClose();
    } catch (e2) {
      setError(e2.message || 'Could not save this post.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open} onClose={() => { if (!saving) onClose(); }} maxWidth="sm" fullWidth
      PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
    >
      <DialogTitle sx={{ fontWeight: 900 }}>{post ? 'Edit Post' : 'New Post'}</DialogTitle>
      <DialogContent>
        <Box component="form" id="board-post-form" onSubmit={handleSubmit} sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField select fullWidth label="Category" value={form.category} onChange={set('category')} required>
            {Object.keys(CATEGORY_LABELS).map(k => <MenuItem key={k} value={k}>{CATEGORY_LABELS[k]}</MenuItem>)}
          </TextField>

          <TextField fullWidth label="Title" value={form.title} onChange={set('title')} required placeholder="Game Day — Saturday" />

          <TextField
            fullWidth multiline minRows={4} label="Message" value={form.body} onChange={set('body')} required
            placeholder="Players at the field by 8:15 AM. White jerseys. Bring water."
          />

          <FormControlLabel
            control={<Checkbox checked={form.isPinned} onChange={e => setForm(f => ({ ...f, isPinned: e.target.checked }))} />}
            label="Pin this post"
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: '#9eb8d0' }}>Cancel</Button>
        <Button type="submit" form="board-post-form" variant="contained" color="primary" disabled={saving} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {saving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : (post ? 'Save Changes' : 'Post')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PostCard({ post, canManage, onEdit, onDeleteClick }) {
  const edited = isPostEdited(post);

  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: post.is_pinned ? '1px solid rgba(245,166,35,0.4)' : '1px solid #1e2a3a' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
              {post.is_pinned && (
                <Chip size="small" label="PINNED" sx={{ fontWeight: 800, bgcolor: '#2a1a05', color: '#f5a623' }} />
              )}
              <Chip size="small" label={CATEGORY_LABELS[post.category] || post.category} sx={{ fontWeight: 700, bgcolor: '#0a2a14', color: '#2ea84b' }} />
            </Box>
            <Typography sx={{ fontWeight: 800 }}>{post.title}</Typography>
            <Typography sx={{ color: '#c8d8e8', fontSize: '0.9rem', mt: 0.5, whiteSpace: 'pre-wrap' }}>{post.body}</Typography>
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.78rem', mt: 1 }}>
              {post.author_name || 'Unknown'} · {new Date(post.created_at).toLocaleString()}
              {edited && ' · edited'}
            </Typography>
          </Box>
          {canManage && (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              <Button size="small" onClick={() => onEdit(post)} sx={{ textTransform: 'none', fontWeight: 700 }}>Edit</Button>
              <Button size="small" color="error" onClick={() => onDeleteClick(post)} sx={{ textTransform: 'none', fontWeight: 700 }}>Delete</Button>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function TeamBoardInner() {
  const { teamId } = useParams();
  const { currentUser } = useAuth();
  const [team, setTeam] = useState(null);
  const [posts, setPosts] = useState([]);
  const [hasProgramAuthority, setHasProgramAuthority] = useState(false);
  const [myRoles, setMyRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const load = async () => {
    const [t, staff, hasAuthority, boardPosts] = await Promise.all([
      getTeam(teamId), listTeamStaff(teamId), hasProgramManagementAuthorityForTeam(teamId), listTeamBoardPosts(teamId),
    ]);
    setTeam(t);
    setMyRoles(staff.filter(s => s.user_id === currentUser?.id && s.status === 'active').map(s => s.role));
    setHasProgramAuthority(hasAuthority);
    setPosts(boardPosts);
  };

  useEffect(() => {
    load().catch(e => setError(e.message)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  // Same "who can manage" shape as create_team_board_post()/update_team_
  // board_post()/delete_team_board_post() (031) — Team Admin/Head Coach,
  // or Program Owner/Admin. Assistant Coach and guardians are read-only;
  // the RPCs independently re-enforce this regardless of what this
  // computes. Authorship alone (post.created_by) is never consulted here
  // — losing a qualifying role removes management of every post,
  // including ones written while it was held.
  const canManage = Boolean(
    hasProgramAuthority || myRoles.includes('team_admin') || myRoles.includes('head_coach')
  );

  // list_team_board_posts() already returns pinned-first, newest-first
  // within each group — filtering preserves that order, no client
  // re-sort needed, and no post appears in both groups.
  const pinned = posts.filter(p => p.is_pinned);
  const others = posts.filter(p => !p.is_pinned);

  const handleAddClick = () => { setEditingPost(null); setFormOpen(true); };
  const handleEditClick = (post) => { setEditingPost(post); setFormOpen(true); };

  const handleDeleteConfirm = async () => {
    setDeleteError(null);
    setDeleting(true);
    try {
      await deleteTeamBoardPost(deleteTarget.id);
      await load();
      setDeleteTarget(null);
    } catch (e) {
      setDeleteError(e.message || 'Could not delete this post.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (error) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{error}</Alert></Container>;
  if (!team) return null;

  return (
    <TeamHubLayout team={team} active="board">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontWeight: 800 }}>Team Board</Typography>
        {canManage && (
          <Button variant="contained" color="primary" size="small" onClick={handleAddClick} sx={{ textTransform: 'none', fontWeight: 700 }}>
            + New Post
          </Button>
        )}
      </Box>

      {posts.length === 0 ? (
        <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography sx={{ color: '#9eb8d0' }}>No posts yet.</Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {pinned.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
              {pinned.map(p => (
                <PostCard key={p.id} post={p} canManage={canManage} onEdit={handleEditClick} onDeleteClick={setDeleteTarget} />
              ))}
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {others.map(p => (
              <PostCard key={p.id} post={p} canManage={canManage} onEdit={handleEditClick} onDeleteClick={setDeleteTarget} />
            ))}
          </Box>
        </>
      )}

      <PostFormDialog open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} teamId={teamId} post={editingPost} />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Delete Post?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>This permanently deletes <strong>{deleteTarget?.title}</strong>. This cannot be undone.</Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 1 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ textTransform: 'none', color: '#9eb8d0' }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} disabled={deleting} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 700 }}>
            {deleting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Delete Post'}
          </Button>
        </DialogActions>
      </Dialog>
    </TeamHubLayout>
  );
}

export default function TeamBoardPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><TeamBoardInner /></RequireAuth>
    </Box>
  );
}
